import { useEditor, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { JSONContent } from "@tiptap/react";
import { useMemo, useRef, useEffect } from "react";
import Heading from "@tiptap/extension-heading";
import { FieldNodeExtension } from "@/components/unified/extensions/FieldNode";
import { SectionNodeExtension } from "@/components/unified/extensions/SectionHeadingNode";
import { ScoreGroupNodeExtension } from "@/components/unified/extensions/ScoreGroupNode";
import {
  SectionVisibilityExtension,
  sectionVisibilityKey,
} from "@/components/unified/extensions/SectionVisibility";
import { UnifiedValuesContext } from "@/components/unified/UnifiedValuesContext";
import type { SubTemplateData } from "@/types/evaluation";

interface Props {
  initialContent: JSONContent;
  values: Record<string, unknown>;
  setFieldValue: (id: string, value: unknown) => void;
  fillVariables: Record<string, string>;
  interpolate: (text: string) => string;
  subTemplateValues: Record<string, SubTemplateData>;
  setSubTemplateValue: (id: string, data: SubTemplateData) => void;
  sectionToggles: Record<string, boolean>;
  onToggleSection: (sectionId: string) => void;
  collapsedSections: Record<string, boolean>;
  onCollapseSection: (sectionId: string) => void;
  fieldToggles: Record<string, boolean>;
  onToggleField: (fieldId: string) => void;
  onDocumentChange: (doc: JSONContent) => void;
  onEditorReady?: (editor: Editor) => void;
}

const editorExtensions = [
  StarterKit.configure({ heading: false }),
  Heading.configure({ levels: [1, 2, 3] }),
  SectionNodeExtension,
  FieldNodeExtension,
  ScoreGroupNodeExtension,
  SectionVisibilityExtension,
];

function useContextValue(props: Readonly<Props>) {
  return useMemo(
    () => ({
      collapsedSections: props.collapsedSections,
      fieldToggles: props.fieldToggles,
      fillVariables: props.fillVariables,
      interpolate: props.interpolate,
      onCollapseSection: props.onCollapseSection,
      onToggleField: props.onToggleField,
      onToggleSection: props.onToggleSection,
      sectionToggles: props.sectionToggles,
      setFieldValue: props.setFieldValue,
      setSubTemplateValue: props.setSubTemplateValue,
      subTemplateValues: props.subTemplateValues,
      values: props.values,
    }),
    [
      props.values,
      props.setFieldValue,
      props.fillVariables,
      props.interpolate,
      props.subTemplateValues,
      props.setSubTemplateValue,
      props.sectionToggles,
      props.onToggleSection,
      props.collapsedSections,
      props.onCollapseSection,
      props.fieldToggles,
      props.onToggleField,
    ],
  );
}

export function UnifiedTiptapEditor(props: Readonly<Props>) {
  const onDocumentChangeRef = useRef(props.onDocumentChange);
  onDocumentChangeRef.current = props.onDocumentChange;

  const onEditorReadyRef = useRef(props.onEditorReady);
  onEditorReadyRef.current = props.onEditorReady;

  const editor = useEditor({
    content: props.initialContent,
    editorProps: {
      handleKeyDown(view, event) {
        if (event.key === "Tab") {
          const { dispatch, state } = view;
          const parentType = state.selection.$from.parent.type.name;
          if (parentType === "paragraph" || parentType === "heading") {
            event.preventDefault();
            dispatch(state.tr.insertText("\t"));
            return true;
          }
        }
        return false;
      },
    },
    extensions: editorExtensions,
    onUpdate: ({ editor }) => {
      onDocumentChangeRef.current(editor.getJSON());
    },
  });

  useEffect(() => {
    onEditorReadyRef.current?.(editor);
  }, [editor]);

  useEffect(() => {
    editor.view.dispatch(
      editor.state.tr.setMeta(sectionVisibilityKey, {
        collapsedSections: props.collapsedSections,
        sectionToggles: props.sectionToggles,
      }),
    );
  }, [props.sectionToggles, props.collapsedSections, editor]);

  const contextValue = useContextValue(props);

  return (
    <UnifiedValuesContext.Provider value={contextValue}>
      <EditorContent editor={editor} className="unified-editor-content" />
    </UnifiedValuesContext.Provider>
  );
}
